import React, { useMemo, useState } from 'react';
import Modal from '@amzn/meridian/modal';
import Text from '@amzn/meridian/text';
import Column from '@amzn/meridian/column';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';

interface FeedbackModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (reasons: string[] | string) => void;
}

const dataEngineeringTeamTicketLink =
    'https://sim.amazon.com/issues/create?template=22689341-290a-4c97-9d04-090d108ab821';

const FeedbackModal = ({ open, onClose, onSubmit }: FeedbackModalProps) => {
    const options = useMemo(
        () => [
            "Wasn't relevant to the question",
            'Contained incorrect information',
            'Contained an inaccurate source link',
            'Was incomplete',
            'Other',
        ],
        [],
    );

    const [checked, setChecked] = useState<Record<string, boolean>>({});
    const [otherText, setOtherText] = useState('');

    const selectedLabelsArray = React.useMemo(
        () =>
            Object.entries(checked)
                .filter(([, v]) => Boolean(v))
                .map(([k]) => k),
        [checked],
    );

    const selectedCount = selectedLabelsArray.length;
    const isOtherSelected = !!checked['Other'];

    const toggle = (label: string) => {
        setChecked(prev => ({ ...prev, [label]: !prev[label] }));
    };

    const handleSubmit = () => {
        const reasons =
            selectedLabelsArray.map(label =>
                label === 'Other' ? (otherText.trim() || 'Other') : label,
            );
        onSubmit(reasons);
    };

    return (
        <Modal
            open={open}
            width={700}
            onClose={onClose}
            scrollContainer="viewport"
            closeLabel="Close"
            aria-describedby="feedback-modal-desc"
        >
            <Column spacingInset="400 500" spacing="300">
                <div className="pf-welcomeModal">Give us feedback to help improve this product</div>
                <Text id="feedback-modal-desc" type="b300">
                    To submit feedback, at least one selection is required.
                </Text>
            </Column>

            <Column spacingInset="400 500" spacing="300">
                <div className="pf-welcomeModal">The response provided:</div>
            </Column>

            <div className="pf-feedbackList">
                {options.map((label) => {
                    const isChecked = !!checked[label];
                    return (
                        <div
                            key={label}
                            className={`pf-feedbackCard ${isChecked ? 'is-selected' : ''}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => toggle(label)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggle(label);
                                }
                            }}
                        >
                            <div className="pf-feedbackText">
                                <Text type="b300">{label}</Text>
                            </div>

                            <div onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                    checked={isChecked}
                                    onChange={() => toggle(label)}
                                    aria-label={label}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
            {isOtherSelected && (
                <div className="pf-otherWrap">
                    <Text type="b200" className="pf-otherLabel">Tell us more</Text>
                    <div className="pf-otherBox">
                    <textarea
                        className="pf-nativeTextarea pf-otherTextarea"
                        value={otherText}
                        onChange={(e) => setOtherText(e.target.value)}
                    />
                    </div>
                </div>
            )}

            <div className="pf-feedbackFooter">
                <Button onClick={onClose}>Maybe later</Button>
                <Button
                    disabled={selectedCount === 0}
                    onClick={handleSubmit}
                >
                    Submit
                </Button>
            </div>

            <div className="pf-feedbackHelp">
                <Text type="b100">
                    Need help?{' '}
                    <a href={dataEngineeringTeamTicketLink} target="_blank" rel="noreferrer">
                        Open a SIM ticket
                    </a>
                </Text>
            </div>
        </Modal>
    );
};

export default FeedbackModal;